import {
  isDateFilterValue,
  isNumberFilterValue,
  isTextFilterConditionArray,
} from '../../hooks/use-filter-type-guards'
import type { FilterFactoryProps } from '../../types/data-table.types'
import { CustomFilter } from './CustomFilter'
import { DateRangeFilter } from './DateRangeFilter'
import { NumberRangeFilter } from './NumberRangeFilter'
import { SelectFilter } from './SelectFilter'
import { TextAdvancedFilter } from './TextAdvancedFilter'
import { TextFilter } from './TextFilter'

export function FilterFactory<TData>({
  column,
  value,
  onChange,
  getFilterConfig,
  getFilterType,
  getFilterTitle,
  getFilterOptions,
  hasCustomFilter,
}: FilterFactoryProps<TData>) {
  const columnId = column.id
  const columnTitle = getFilterTitle(column)
  const filterType = getFilterType(column)
  const config = getFilterConfig(column)

  const getStringValue = () => (typeof value === 'string' ? value : undefined)
  const getDateValue = () => (isDateFilterValue(value) ? value : undefined)
  const getNumberValue = () => (isNumberFilterValue(value) ? value : undefined)
  const getTextAdvancedValue = () =>
    isTextFilterConditionArray(value) ? value : undefined

  switch (filterType) {
    case 'date':
      return (
        <DateRangeFilter
          columnId={columnId}
          columnTitle={columnTitle}
          value={getDateValue()}
          onChange={(val) => onChange(val)}
        />
      )

    case 'select':
      return (
        <SelectFilter
          columnId={columnId}
          columnTitle={columnTitle}
          value={getStringValue()}
          onChange={(val) => onChange(val)}
          options={getFilterOptions(column)}
          placeholder={config.filterPlaceholder}
        />
      )

    case 'number':
      return (
        <NumberRangeFilter
          columnId={columnId}
          columnTitle={columnTitle}
          value={getNumberValue()}
          onChange={(val) => onChange(val)}
        />
      )

    case 'text-advanced':
      return (
        <TextAdvancedFilter
          columnId={columnId}
          columnTitle={columnTitle}
          value={getTextAdvancedValue()}
          onChange={(val) => onChange(val)}
        />
      )

    case 'custom':
      if (hasCustomFilter(column)) {
        return (
          <CustomFilter
            columnId={columnId}
            columnTitle={columnTitle}
            value={value}
            onChange={onChange}
            config={config}
          />
        )
      }
      // Fallback to text if custom filter is not properly configured
      return (
        <TextFilter
          columnId={columnId}
          columnTitle={columnTitle}
          value={getStringValue()}
          onChange={(val) => onChange(val)}
        />
      )

    default: // text
      return (
        <TextFilter
          columnId={columnId}
          columnTitle={columnTitle}
          value={getStringValue()}
          onChange={(val) => onChange(val)}
        />
      )
  }
}
